/**
 * profileEffects.js
 * ----------------------------------------------------------------------------
 * Shared per-effect element generators used by both:
 *   - The public profile page (`/isper/:username`)        →  Profile.js
 *   - The dashboard "Customize profile" live preview      →  Dashboard.js
 *
 * Previously each component had its own loop building generic snow-shaped
 * elements, which meant the Dashboard preview missed the per-effect
 * width/height/top/transform/fontSize set by Profile's generators — so
 * effects like stars / soundwave / streaks / bubbles / matrix / fireflies
 * looked broken (or invisible) inside the preview card. Centralising them
 * here keeps both renderings byte-identical.
 *
 * Each generator returns: [{ key, cls, style, text? }]
 * The CSS classes (.pfx-snow, .pfx-star, .pfx-wave, …) live in Profile.css.
 * ----------------------------------------------------------------------------
 */

const generateSnowflakes = (count = 30) =>
  Array.from({ length: count }, (_, i) => ({
    key: `snow-${i}`,
    cls: 'pfx-snow',
    style: {
      left: `${Math.random() * 100}%`,
      animationDuration: `${3 + Math.random() * 5}s`,
      animationDelay: `${Math.random() * 6}s`,
      width: `${4 + Math.random() * 6}px`,
      height: `${4 + Math.random() * 6}px`,
      opacity: 0.5 + Math.random() * 0.5,
    },
    text: '',
  }));

const generateParticles = (count = 50) =>
  Array.from({ length: count }, (_, i) => ({
    key: `p-${i}`,
    cls: 'pfx-particle',
    style: {
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDuration: `${5 + Math.random() * 8}s`,
      animationDelay: `${Math.random() * 8}s`,
      width: `${2 + Math.random() * 4}px`,
      height: `${2 + Math.random() * 4}px`,
    },
    text: '',
  }));

const generateStars = (count = 40) =>
  Array.from({ length: count }, (_, i) => ({
    key: `s-${i}`,
    cls: 'pfx-star',
    style: {
      left: `${-10 + Math.random() * 60}%`,
      top: `${-20 + Math.random() * 50}%`,
      animationDuration: `${4 + Math.random() * 6}s`,
      animationDelay: `${Math.random() * 8}s`,
      width: `${2 + Math.random() * 3}px`,
      height: `${2 + Math.random() * 3}px`,
    },
    text: '',
  }));

const generateSoundwave = (count = 40) =>
  Array.from({ length: count }, (_, i) => ({
    key: `w-${i}`,
    cls: 'pfx-wave',
    style: {
      left: `${(i / count) * 100}%`,
      width: `${100 / count - 0.4}%`,
      animationDuration: `${0.5 + Math.random() * 1}s`,
      animationDelay: `${Math.random() * 0.8}s`,
      height: `${30 + Math.random() * 90}px`,
    },
    text: '',
  }));

const generateStreaks = (count = 15) =>
  Array.from({ length: count }, (_, i) => ({
    key: `st-${i}`,
    cls: 'pfx-streak',
    style: {
      top: `${Math.random() * 90}%`,
      left: `${Math.random() * 50}%`,
      animationDuration: `${2 + Math.random() * 2.5}s`,
      animationDelay: `${Math.random() * 8}s`,
      width: `${100 + Math.random() * 200}px`,
      transform: `rotate(${-30 + Math.random() * 25}deg)`,
    },
    text: '',
  }));

const generateBubbles = (count = 28) =>
  Array.from({ length: count }, (_, i) => {
    const size = 10 + Math.random() * 26;
    return {
      key: `b-${i}`,
      cls: 'pfx-bubble',
      style: {
        left: `${Math.random() * 100}%`,
        bottom: `-${40 + Math.random() * 60}px`,
        width: `${size}px`,
        height: `${size}px`,
        animationDuration: `${6 + Math.random() * 8}s`,
        animationDelay: `${Math.random() * 10}s`,
        opacity: 0.3 + Math.random() * 0.5,
      },
      text: '',
    };
  });

const generateMatrix = (count = 22) => {
  const chars = '01ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ';
  return Array.from({ length: count }, (_, i) => ({
    key: `m-${i}`,
    cls: 'pfx-matrix',
    text: Array.from({ length: 14 + Math.floor(Math.random() * 20) }, () =>
      chars[Math.floor(Math.random() * chars.length)]
    ).join('\n'),
    style: {
      left: `${(i / count) * 100 + (Math.random() * 3 - 1.5)}%`,
      animationDuration: `${6 + Math.random() * 8}s`,
      animationDelay: `${Math.random() * 6}s`,
      fontSize: `${12 + Math.random() * 6}px`,
      opacity: 0.35 + Math.random() * 0.55,
    },
  }));
};

const generateFireflies = (count = 30) =>
  Array.from({ length: count }, (_, i) => {
    const size = 3 + Math.random() * 4;
    return {
      key: `ff-${i}`,
      cls: 'pfx-firefly',
      style: {
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDuration: `${4 + Math.random() * 6}s`,
        animationDelay: `${Math.random() * 6}s`,
      },
      text: '',
    };
  });

const generateWarpSpeed = (count = 60) =>
  Array.from({ length: count }, (_, i) => {
    // Stars originate from center and fly outward in all directions
    const angle = Math.random() * 360;               // direction in degrees
    const startDist = Math.random() * 5;              // start near center (%)
    const size = 1 + Math.random() * 3;               // small dots
    const duration = 4 + Math.random() * 6;           // moderate speed (not too fast)
    const delay = Math.random() * 8;
    // Convert angle to radians for positioning
    const rad = (angle * Math.PI) / 180;
    const startX = 50 + Math.cos(rad) * startDist;
    const startY = 50 + Math.sin(rad) * startDist;
    return {
      key: `warp-${i}`,
      cls: 'pfx-warp',
      style: {
        left: `${startX}%`,
        top: `${startY}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`,
        '--warp-angle': `${angle}deg`,
        '--warp-tx': `${Math.cos(rad) * 120}vmax`,
        '--warp-ty': `${Math.sin(rad) * 120}vmax`,
        opacity: 0,
      },
      text: '',
    };
  });

export const EFFECT_GENERATORS = {
  snowflakes: generateSnowflakes,
  particles: generateParticles,
  stars: generateStars,
  soundwave: generateSoundwave,
  streaks: generateStreaks,
  bubbles: generateBubbles,
  matrix: generateMatrix,
  fireflies: generateFireflies,
  warpspeed: generateWarpSpeed,
  none: () => [],
};

// Element counts to use inside the small Dashboard preview card. Tuned down
// from the full-page values so we don't bury the preview in particles.
export const PREVIEW_EFFECT_COUNTS = {
  snowflakes: 14,
  particles: 22,
  stars: 18,
  soundwave: 24,
  streaks: 8,
  bubbles: 14,
  matrix: 12,
  fireflies: 16,
  warpspeed: 30,
  none: 0,
};

/**
 * Generate effect elements scaled for the preview pane. Falls back to the
 * generator's own default count if no preview-specific value is set.
 */
export function generatePreviewEffects(effect) {
  const gen = EFFECT_GENERATORS[effect];
  if (!gen) return [];
  const count = PREVIEW_EFFECT_COUNTS[effect];
  return typeof count === 'number' ? gen(count) : gen();
}
